import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import styles from "../../css/IndividualReport.module.css";

/**
 * RecordDetailDialog
 *
 * Props:
 *  - record   {object|null}  — the flattened row object; null = closed
 *  - type     {string}       — "recitation" | "homework"
 *  - tokens   {object}       — MEM (yellow) or REV (pink) color tokens
 *  - onClose  {fn}           — called when dialog should close
 */
export const RecordDetailDialog = ({ record, type, tokens, onClose }) => {
  const { t, i18n } = useTranslation();
 
  // Close on Escape
  useEffect(() => {
    if (!record) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [record, onClose]);
 
  // Prevent body scroll while open
  useEffect(() => {
    document.body.style.overflow = record ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [record]);
 
  if (!record) return null;
 
  const accent  = tokens?.accent  ?? "#666";
  const hdrBg   = tokens?.hdrBg   ?? "#F5F4EF";
  const border  = tokens?.border  ?? "#EEE";
  const bg      = tokens?.bg      ?? "#FAFAF8";
  const statNum = tokens?.statNum ?? "#333";
 
  const isRec = type === "recitation";
 
  return (
    <>
      {/* Backdrop */}
      <div className={styles.dialogBackdrop} onClick={onClose} />
 
      {/* Dialog */}
      <div className={styles.dialog} role="dialog" aria-modal="true">
 
        {/* Header */}
        <div className={styles.dialogHeader} style={{ background: hdrBg, borderBottom: `1px solid ${border}` }}>
          <span className={styles.dialogTitle} style={{ color: accent }}>
            {isRec
              ? t("admin_pages.quran_report.card_titles.recitation_records")
              : t("admin_pages.quran_report.card_titles.homework_records")}
          </span>
          <button className={styles.dialogClose} onClick={onClose} aria-label="Close">✕</button>
        </div>
 
        {/* Body — no dividers between sections */}
        <div className={styles.dialogBody}>
 
          {/* Date — label and value on the same line */}
          <div className={styles.dialogSection} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span className={styles.dialogFieldValue}>{record.activity_date || "—"}</span>
          </div>
 
          {/* Reference */}
          <div className={styles.dialogSection}>
            <span className={styles.dialogFieldLabel}>
              {t("admin_pages.quran_report.table_cols.reference")}
            </span>
            <p className={styles.dialogNotes}>
              {record.ref || "—"}
            </p>
          </div>
 
          {/* Score + Mistakes (recitation only) */}
          {isRec && (
            <div className={styles.dialogSection}>
              <div className={styles.dialogStatsRow}>
                <div className={styles.dialogStat} style={{ background: bg, border: `1px solid ${border}` }}>
                  <div className={styles.dialogStatNum} style={{ color: statNum }}>
                    {record.score ?? "—"}
                  </div>
                  <div className={styles.dialogStatLbl}>
                    {t("admin_pages.quran_report.table_cols.score")}
                  </div>
                </div>
                <div className={styles.dialogStat} style={{ background: bg, border: `1px solid ${border}` }}>
                  <div className={styles.dialogStatNum} style={{ color: statNum }}>
                    {record.mistakes_telawa ?? 0}
                  </div>
                  <div className={styles.dialogStatLbl}>
                    {t("admin_pages.quran_report.table_cols.mistakes_tilawa")}
                  </div>
                </div>
                <div className={styles.dialogStat} style={{ background: bg, border: `1px solid ${border}` }}>
                  <div className={styles.dialogStatNum} style={{ color: statNum }}>
                    {record.mistakes_tagweed ?? 0}
                  </div>
                  <div className={styles.dialogStatLbl}>
                    {t("admin_pages.quran_report.table_cols.mistakes_tajweed")}
                  </div>
                </div>
              </div>
            </div>
          )}
 
          {/* Notes */}
          <div className={styles.dialogSection}>
            <span className={styles.dialogFieldLabel}>
              {t("admin_pages.quran_report.table_cols.notes")}
            </span>
            <p className={styles.dialogNotes}>
              {record.note && record.note !== "—" ? record.note : (
                <span style={{ color: "#BBB", fontStyle: "italic" }}>
                  {t("admin_pages.quran_report.card_titles.no_records")}
                </span>
              )}
            </p>
          </div>
 
        </div>
 
        {/* Footer */}
        <div className={styles.dialogFooter} style={{ borderTop: `1px solid ${border}` }}>
          <button
            className={styles.dialogCloseBtn}
            style={{ background: accent }}
            onClick={onClose}
          >
            {t("admin_pages.quran_report.filters.filter") === "Filter"
              ? "Close"
              : t("admin_pages.quran_report.dialog.close")}
          </button>
        </div>
 
      </div>
    </>
  );
};
 